'use client'

import { useState } from 'react'

import { Accordion } from '@/app/components/atoms/Accordion'
import { Chip } from '@/app/components/atoms/Chip'
import { useLocationHash } from '@/app/lib/hooks/useLocationHash'

import styles from './FaqAccordion.module.scss'
import type { FaqAccordionGroupsProps } from './FaqAccordion.types'
import { toAccordionItems } from './utils/toAccordionItems'

/**
 * `FaqAccordionGroups` — maquetación del acordeón **con chips de grupo**:
 * una chip por grupo y un solo listado visible a la vez, el del grupo activo
 * (misma interacción que las categorías de `/ayuda` en el proyecto de
 * referencia, `HelpPageClient`).
 *
 * Interno a `FaqAccordion`: no se exporta en `index.ts`. `FaqAccordion` decide
 * en servidor si toca esta maquetación (hay `group` en el payload) o el
 * listado único, y le pasa los grupos ya resueltos y saneados
 * (`groupFaqEntries`) — aquí no se sanea ni se agrupa nada.
 *
 * Client Component: la única razón es el grupo seleccionado. Las chips son
 * `<button aria-pressed>` (átomo `Chip`) dentro de una lista etiquetada, el
 * mismo patrón que el resto de filtros del proyecto (`ListingFilters`) — no un
 * `tablist`, porque el listado que hay debajo no es un panel de pestaña sino
 * el mismo acordeón con otro contenido.
 */
export function FaqAccordionGroups({
  groups,
  className,
  groupsLabel,
}: FaqAccordionGroupsProps) {
  const [activeLabel, setActiveLabel] = useState(groups[0]?.label ?? '')

  // Un `#ancla` a una pregunta de otro grupo activa ese grupo primero; el
  // `Accordion` del grupo (remontado por `key`) abre luego la pregunta.
  const hash = useLocationHash()
  const [handledHash, setHandledHash] = useState('')
  if (hash !== handledHash) {
    setHandledHash(hash)
    const hashGroup = groups.find((group) =>
      group.entries.some((entry) => entry.anchorId === hash),
    )
    if (hashGroup) setActiveLabel(hashGroup.label)
  }
  const activeGroup = groups.find((group) => group.label === activeLabel) ?? groups[0]

  if (!activeGroup) return null

  return (
    <>
      <ul className={styles.groups} aria-label={groupsLabel}>
        {groups.map((group) => (
          <li key={group.label} className={styles.groupItem}>
            <Chip
              label={group.label}
              selected={group.label === activeGroup.label}
              onClick={() => setActiveLabel(group.label)}
            />
          </li>
        ))}
      </ul>
      {/* `key`: al cambiar de grupo se remonta el acordeón, de modo que el
          listado nuevo aparece con todas las preguntas colapsadas en vez de
          heredar la que estuviera abierta en el grupo anterior (mismo criterio
          que `HelpPageClient` en el proyecto de referencia). */}
      <Accordion
        key={activeGroup.label}
        className={className}
        items={toAccordionItems(activeGroup.entries)}
      />
    </>
  )
}
